// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Helmet from 'react-helmet';
import useSheet from 'app/jss';
import PurchaseRow from 'app/components/giftstore/PurchaseRow';
import Title from 'app/components/commons/Title';
import Spinner from 'app/components/commons/Spinner';
import * as giftstoreActions from 'app/actions/giftstoreActions';
import PaginationBox from 'app/components/commons/PaginationBox';
import RowHeader from 'app/components/commons/RowHeader';
import Responsive from 'app/components/responsive/Responsive';
import t from 'app/utils/translate';

type Props = {
  fetchPurchases: () => void,
  giftStoreName: string,
  pagination: $Shape<{
    current_page: number,
    last_page: number,
  }>,
  isLoading?: boolean,
  purchases?: Array<Object>,
  sheet: Object,
};

class GiftPurchases extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    isLoading: false,
    purchases: [],
  };

  componentDidMount() {
    const { fetchPurchases } = this.props;

    fetchPurchases();
  }

  render() {
    const {
      fetchPurchases,
      giftStoreName,
      isLoading,
      pagination,
      purchases,
      sheet: { classes },
    } = this.props;

    return (
      <div>
        <Helmet title={t('Purchases')} />
        <Title>
          {t('Purchases')}
        </Title>
        <hr />
        {isLoading ? (
          <div className={classes.loader}>
            <Spinner />
          </div>
        ) : (
          <div>
            <PaginationBox
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onChange={fetchPurchases}
            />
            <div className={classes.table}>
              <Responsive desktop tabletLandscape>
                {isDesktop => (
                  <RowHeader isTableHeader first>
                    <div className={classNames(classes.tableCell, classes.store)}>
                      {t('Product')}
                    </div>
                    <div className={classNames(classes.tableCell, classes.tableCell__small)}>
                      {t('Date')}
                    </div>
                    <div className={classNames(classes.tableCell, classes.tableCell__small)}>
                      {t('Cost')}
                    </div>
                    {isDesktop && (
                      <div className={classNames(classes.tableCell, classes.tableCell__large)}>
                        {t('Details')}
                      </div>
                    )}
                  </RowHeader>
                )}
              </Responsive>
              {purchases.length === 0 && (
                <div className={classes.noPurchasesContainer}>
                  <span>
                    {t('You have no purchases. Go to the *gift_store_name* to purchase a gift!').replace('*gift_store_name*', t(giftStoreName))}
                  </span>
                </div>
              )}
              <div className={classes.rows}>
                {purchases.map(p => (
                  <PurchaseRow
                    classes={classes}
                    createdAt={p.createdAt}
                    credentialInfo={p.credentialInfo}
                    expiryDate={p.expiryDate}
                    key={p.id}
                    name={p.name}
                    pin={p.pin}
                    price={p.price}
                    status={p.status}
                    voucherCode={p.voucherCode}
                  />
                ))}
              </div>
            </div>
            <PaginationBox
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onChange={fetchPurchases}
            />
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    giftStoreName: state.settings.giftStoreName,
    isLoading: state.giftstore.isLoading,
    pagination: state.giftstore.purchasesPagination,
    purchases: state.giftstore.purchases,
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return {
    fetchPurchases(page) {
      dispatch(giftstoreActions.fetchPurchases({ page }));
    },
  };
}

function applyStyles(theme) {
  return {
    loader: {
      display: 'flex',
      justifyContent: 'center',
      padding: 20,
    },
    tableCell: {
      flexShrink: 0,
      paddingLeft: theme.spacing.xsmall,
      paddingRight: theme.spacing.xsmall,
    },
    tableCell__small: {
      width: 90,
    },
    tableCell__large: {
      width: 180,
    },
    store: {
      flexGrow: 1,
      display: 'flex',
    },
    value: {
      minWidth: '7em',
      width: '7em',
      color: theme.color.basis3,
    },
    subtext: {
      color: theme.color.basis2,
      fontSize: theme.fontSize.small,
      wordBreak: 'break-all',
    },
    pill: {
      // alignSelf: 'flex-start',
      borderRadius: theme.borderRadius.small,
      color: theme.color.white,
      fontSize: theme.fontSize.xtiny,
      lineHeight: 1.1,
      paddingTop: 3,
      paddingRight: theme.spacing.small,
      paddingBottom: 3,
      paddingLeft: theme.spacing.small,
      textAlign: 'center',
      textTransform: 'uppercase',
      '&.approved': {
        background: theme.color.success,
      },
      '&.pending': {
        background: theme.color.warning,
      },
      '&.rejected': {
        background: theme.color.danger,
      },
    },
    noPurchasesContainer: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'center',
      padding: theme.spacing.default,
    },
    [theme.mediaQuery.tabletPortraitAndUp]: {
      tableCell__small: {
        minWidth: 0,
        width: 140,
      },
      tableCell__large: {
        minWidth: 0,
        width: 280,
      },
    },
    [theme.mediaQuery.desktopAndUp]: {
      tableCell__small: {
        minWidth: 0,
        width: 180,
      },
      tableCell__large: {
        minWidth: 0,
        width: 360,
      },
    },
  };
}

export default useSheet(applyStyles)(connect(mapStateToProps, mapDispatchToProps)(GiftPurchases));
